import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Smile } from "lucide-react";

const EMOJI_GROUPS = [
  {
    label: "Smileys",
    emojis: ["😀", "😁", "😂", "🤣", "😊", "😍", "🥰", "😘", "😜", "🤗", "😎", "🤩", "😇", "🙂", "😉", "😋", "🤔", "🤫", "😏", "😶"],
  },
  {
    label: "Gestures",
    emojis: ["👍", "👎", "👋", "🤝", "🙏", "💪", "👏", "🤞", "✌️", "🤟", "👌", "🫶", "❤️", "🔥", "⭐", "✨", "💯", "🎉", "🎊", "💐"],
  },
  {
    label: "Shopping",
    emojis: ["🛍️", "🛒", "📦", "🏷️", "💰", "💳", "🧾", "📬", "✅", "❌", "⏰", "🚚", "📱", "👕", "👗", "👖", "👟", "👜", "🧥", "🎁"],
  },
];

export default function EmojiPicker({ onSelect, position = "top" }) {
  const [open, setOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(0);
  const [panelStyle, setPanelStyle] = useState({});
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const updatePanelPosition = () => {
      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const horizontalPadding = 12;
      const verticalPadding = 12;
      const panelWidth = Math.min(320, viewportWidth - horizontalPadding * 2);
      const availableAbove = rect.top - verticalPadding;
      const availableBelow = viewportHeight - rect.bottom - verticalPadding;
      const renderAbove = position === "top"
        ? availableAbove >= 220 || availableAbove >= availableBelow
        : availableAbove > availableBelow && availableAbove >= 220;

      const maxHeight = Math.max(
        180,
        Math.min(320, renderAbove ? availableAbove - 8 : availableBelow - 8)
      );

      const left = Math.min(
        Math.max(horizontalPadding, rect.right - panelWidth),
        viewportWidth - panelWidth - horizontalPadding
      );

      setPanelStyle(renderAbove
        ? {
            position: "fixed",
            left,
            bottom: Math.max(verticalPadding, viewportHeight - rect.top + 8),
            width: panelWidth,
            maxHeight,
          }
        : {
            position: "fixed",
            left,
            top: Math.max(verticalPadding, rect.bottom + 8),
            width: panelWidth,
            maxHeight,
          });
    };

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);

    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [open, position]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const target = e.target;
      const clickedTrigger = pickerRef.current?.contains(target);
      const clickedPanel = panelRef.current?.contains(target);
      if (!clickedTrigger && !clickedPanel) setOpen(false);
    };

    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  return (
    <div
      className="relative"
      ref={pickerRef}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/70 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 dark:focus-visible:ring-neutral-500/70 md:h-10 md:w-10"
        aria-label="Emoji picker"
        aria-expanded={open}
      >
        <Smile size={16} />
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          onMouseDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
          className="theme-menu-panel z-[110]"
        >
          <div className="flex flex-wrap gap-1 border-b px-2 py-2 theme-menu-divider">
            {EMOJI_GROUPS.map((g, i) => (
              <button
                key={g.label}
                type="button"
                onClick={() => setActiveGroup(i)}
                className={`rounded-full px-2.5 py-1 text-[10px] font-semibold transition ${
                  i === activeGroup
                    ? "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100"
                    : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/70 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 dark:focus-visible:ring-neutral-500/70"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto p-2" style={{ maxHeight: panelStyle.maxHeight ? Math.max(120, panelStyle.maxHeight - 48) : 240 }}>
            <div className="grid grid-cols-7 gap-1 sm:grid-cols-8">
              {EMOJI_GROUPS[activeGroup].emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    onSelect(emoji);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-xl text-neutral-900 transition hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400/70 dark:text-neutral-50 dark:hover:bg-neutral-800 dark:focus-visible:ring-neutral-500/70"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
